import { ArrowLeft, EnvelopeSimple } from '@phosphor-icons/react';
import { useState } from 'react';
import a from './SignIn.module.css';
import { supabase } from '../lib/supabase';
import { Button } from '../ui/kit';

type Stage = { at: 'form' } | { at: 'sending' } | { at: 'sent'; email: string } | { at: 'error'; message: string };

/**
 * Sign in with a link sent by email. Supabase sends it and handles the return, so there is no
 * password to choose, forget or leak.
 */
export function SignIn({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<Stage>({ at: 'form' });

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    if (!supabase) return setStage({ at: 'error', message: 'This build has no Supabase project configured, so signing in is not possible here.' });
    setStage({ at: 'sending' });
    const { error } = await supabase.auth.signInWithOtp({
      email: address,
      options: { emailRedirectTo: window.location.origin },
    });
    setStage(error ? { at: 'error', message: error.message } : { at: 'sent', email: address });
  };

  return (
    <div className={a.page}>
      <main className={a.card}>
        <h1>Sign in to Extent</h1>
        {stage.at === 'sent' ? (
          <>
            <p className={a.lede}>
              A sign-in link is on its way to <b>{stage.email}</b>. Open it on this device; it works once and
              expires after an hour.
            </p>
            <p className={a.note}>Nothing arrived? Check the spam folder, or try again in a minute.</p>
            <Button onClick={() => setStage({ at: 'form' })}>Use a different address</Button>
          </>
        ) : (
          <>
            <p className={a.lede}>
              Enter your work address and we will send a link that signs you in. No password to choose or forget.
            </p>
            <form onSubmit={send} className={a.form}>
              <label className={a.label} htmlFor="signin-email">Email address</label>
              <input
                id="signin-email" className={a.input} type="email" autoComplete="email" required autoFocus
                placeholder="you@yourfirm.com" value={email} onChange={(e) => setEmail(e.target.value)}
                disabled={stage.at === 'sending'}
              />
              <Button variant="primary" type="submit" disabled={stage.at === 'sending' || !email.trim()} icon={<EnvelopeSimple size={15} />}>
                {stage.at === 'sending' ? 'Sending…' : 'Send the sign-in link'}
              </Button>
            </form>
            {stage.at === 'error' && <p className={a.error} role="alert">{stage.message}</p>}
          </>
        )}
        <button type="button" className={a.back} onClick={onBack}>
          <ArrowLeft size={13} /> Back to projects in this browser
        </button>
      </main>
    </div>
  );
}
